import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { NDATemplate } from 'models/Nda';
import ArchivedNdasModal from 'components/NdaListModal/components/ArchivedNdasModal';
import ActiveNdasModal from './components/ActiveNdasModal';
import { sentryError } from 'utils';
import api from 'api';
import { filterNdaTemplates } from './utils';
import { useTranslation } from 'react-i18next';

import './NdaListModal.styles.scss';

type NdaListModalProps = {
  className?: string;
  title?: string;
  description?: string;
  onSelectNda: (template: NDATemplate) => Promise<void>;
};

const NdaListModal: ForwardRefRenderFunction<OverlayRef, NdaListModalProps> = (
  props,
  ref,
) => {
  const { className, title, description, onSelectNda } = props;

  const [templates, setTemplates] = useState<NDATemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [archivedTemplates, setArchivedTemplates] = useState<NDATemplate[]>([]);
  const [isArchivedLoading, setArchivedIsLoading] = useState(true);

  const [activeModal, activeModalRef] = useCallbackRef<OverlayRef>();
  const [archivedModal, archivedModalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => activeModal, [activeModal]);

  const classes = classNames('nda-list-modal', className);

  const { t } = useTranslation();

  const onOpenArchivedModal = useCallback(() => {
    activeModal?.close();
    archivedModal?.open();
  }, [activeModal, archivedModal]);

  const onOpenActiveModal = useCallback(() => {
    archivedModal?.close();
    activeModal?.open();
  }, [activeModal, archivedModal]);

  const filterTemplates = useCallback(
    (templateId: string) =>
      setTemplates((old) => filterNdaTemplates(old, templateId)),
    [],
  );

  const filterArchivedTemplates = useCallback(
    (templateId: string) =>
      setArchivedTemplates((old) => filterNdaTemplates(old, templateId)),
    [],
  );

  const addTemplate = useCallback(
    (template: NDATemplate) => setTemplates((old) => [...old, template]),
    [],
  );

  const addArchivedTemplate = useCallback(
    (template: NDATemplate) =>
      setArchivedTemplates((old) => [...old, template]),
    [],
  );

  const onAddActiveTemplate = useCallback(
    (template: NDATemplate) => {
      filterArchivedTemplates(template.templateId);
      addTemplate(template);
    },
    [addTemplate, filterArchivedTemplates],
  );

  const onAddArchivedTemplate = useCallback(
    (template: NDATemplate) => {
      filterTemplates(template.templateId);
      addArchivedTemplate(template);
    },
    [addArchivedTemplate, filterTemplates],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.general.getNdaTemplates();
        setTemplates(data);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setArchivedIsLoading(true);
        const { data } = await api.general.getArchivedNdaTemplates();
        setArchivedTemplates(data);
      } catch (error) {
        sentryError(error);
      } finally {
        setArchivedIsLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <ActiveNdasModal
        ref={activeModalRef}
        className={classes}
        title={title || t('connectNondisclosureAgreement')}
        description={description}
        templates={templates}
        isLoading={isLoading}
        onSelectNda={onSelectNda}
        onOpenArchivedModal={onOpenArchivedModal}
        onAddActiveTemplate={onAddActiveTemplate}
        onAddArchivedTemplate={onAddArchivedTemplate}
        filterTemplates={filterTemplates}
        addTemplate={addTemplate}
      />
      <ArchivedNdasModal
        ref={archivedModalRef}
        className={classes}
        templates={archivedTemplates}
        isLoading={isArchivedLoading}
        onOpenActiveModal={onOpenActiveModal}
        onAddActiveTemplate={onAddActiveTemplate}
        onAddArchivedTemplate={onAddArchivedTemplate}
      />
    </>
  );
};

export default forwardRef(NdaListModal);
